import type { CSSProperties } from "react";

/** Font spec: plain family name or { family, weight? } */
type FontSpec = string | { family: string; weight?: number };

/** Normalize a FontSpec into { family, weight? } */
function normalizeFontSpec(spec: FontSpec): {
  family: string;
  weight?: number;
} {
  return typeof spec === "string" ? { family: spec } : spec;
}

/**
 * Sanitizes a Google Font family name: keeps only characters that are valid in
 * a Google Fonts family name (letters, digits, spaces, and hyphens).
 * Returns null if the result is empty (invalid name).
 */
function sanitizeFontName(name: string): string | null {
  const cleaned = name.replace(/[^a-zA-Z0-9 -]/g, "").trim();
  return cleaned.length > 0 ? cleaned : null;
}

/** Default weight range loaded from Google Fonts */
const DEFAULT_WEIGHTS = [300, 400, 600, 700];

/**
 * Dynamically injects (or updates) a Google Fonts <link> element for custom
 * per-narrative font families.
 *
 * A single element with id="narrative-fonts" is reused on repeated calls so
 * that switching between narratives never accumulates redundant <link> nodes.
 */
export function loadNarrativeFonts(
  headerFont?: FontSpec,
  bodyFont?: FontSpec,
): void {
  const families: string[] = [];

  const header = headerFont ? normalizeFontSpec(headerFont) : null;
  const body = bodyFont ? normalizeFontSpec(bodyFont) : null;

  const cleanHeader = header ? sanitizeFontName(header.family) : null;
  const cleanBody = body ? sanitizeFontName(body.family) : null;

  const toFamilyParam = (name: string, extraWeights: number[]) => {
    const weights = [...new Set([...DEFAULT_WEIGHTS, ...extraWeights])].sort(
      (a, b) => a - b,
    );
    return `family=${name.replace(/\s+/g, "+")}:wght@${weights.join(";")}`;
  };

  if (cleanHeader && cleanBody && cleanHeader === cleanBody) {
    // Same family — merge both weights into one request
    const extra = [header!.weight, body!.weight].filter(
      (w): w is number => w != null,
    );
    families.push(toFamilyParam(cleanHeader, extra));
  } else {
    if (cleanHeader)
      families.push(
        toFamilyParam(cleanHeader, header!.weight ? [header!.weight] : []),
      );
    if (cleanBody)
      families.push(
        toFamilyParam(cleanBody, body!.weight ? [body!.weight] : []),
      );
  }

  if (families.length === 0) return;

  const href = `https://fonts.googleapis.com/css2?${families.join("&")}&display=swap`;

  const existing = document.getElementById(
    "narrative-fonts",
  ) as HTMLLinkElement | null;

  if (existing) {
    existing.href = href;
  } else {
    const link = document.createElement("link");
    link.id = "narrative-fonts";
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }
}

/**
 * Builds an inline-style object that scopes CSS custom property overrides to
 * the narrative container. Returns an empty object when no custom fonts are
 * specified so the defaults (DM Serif Text / DM Sans) take effect.
 *
 * Sets --font-narrative-header / --font-narrative-body for family, and
 * --font-narrative-header-weight / --font-narrative-body-weight when a
 * weight is specified.
 */
export function narrativeFontStyle(
  headerFont?: FontSpec,
  bodyFont?: FontSpec,
): CSSProperties {
  const style: Record<string, string> = {};

  const header = headerFont ? normalizeFontSpec(headerFont) : null;
  const body = bodyFont ? normalizeFontSpec(bodyFont) : null;

  const cleanHeader = header ? sanitizeFontName(header.family) : null;
  const cleanBody = body ? sanitizeFontName(body.family) : null;

  if (cleanHeader) {
    style["--font-narrative-header"] =
      `"${cleanHeader}", var(--font-serif), serif`;
    if (header!.weight) {
      style["--font-narrative-header-weight"] = String(header!.weight);
    }
  }
  if (cleanBody) {
    style["--font-narrative-body"] =
      `"${cleanBody}", var(--font-sans), sans-serif`;
    if (body!.weight) {
      style["--font-narrative-body-weight"] = String(body!.weight);
    }
  }

  return style as CSSProperties;
}
